/**
 * Offener Bedarf — Zeilen mit den OFFENEN Bedarfszeilen der Woche (filled < BedarfAnzahl) als Chips je Tag
 * (reference/offener-bedarf/OFFENER_BEDARF.md). Klick auf einen Chip öffnet die bekannte Bedarf-Zuordnung.
 *
 * Zwei Varianten:
 * - Wochenplan: je Bereich eine Zeile direkt unter dem Bereichskopf, nur wenn der Bereich offene Bedarfe hat
 *   (Planer planen ihren Bereich mit allen Posten). Die Zellen tragen data-bereich und klappen so mit dem
 *   Bereich ein und aus.
 * - Mitarbeiteransicht (keine Bereichsgruppierung): eine Zeile unter den Spaltenköpfen über alle Bereiche,
 *   inkl. „Bedarf gedeckt“-Zustand, wenn in der Woche alles besetzt ist.
 *
 * Abweichungen von der Referenz (abgestimmt): nicht sticky, kein Bereichspunkt (Bereich als kleiner Text
 * hinter dem Posten), Event-Bedarfe in Farbe + Icon des Events, kein „Alle zuweisen“, statt „+n weitere“
 * scrollt die Tageszelle ab ca. drei Chips, Schriftgrößen wie die Schichtkarten, kein Tablet-Kompaktmodus.
 *
 * Layout je Zeile: .blbl (Spalten Name + Status) · 7× .bcell · .bsum — dieselben 10 Spalten wie die
 * Datenzeilen.
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { bedarfService } from '../services/BedarfService.js';
import { eventService } from '../services/EventService.js';
import { datumService } from '../services/DatumService.js';
import { oeffneBedarfZuordnung } from '../dialogs/bedarfZuordnung.js';
import { oeffneEventEditor } from '../dialogs/eventEditor.js';
import { stateCls } from '../util/bedarfState.js';
import { eventFarbe } from '../util/eventFarben.js';
import { kurzZeit } from './bedarfChip.js';
import {
	istBereichSichtbar,
	istPostenSichtbar,
	istQualifikationSichtbar,
	istEinsatzortSichtbar,
} from './filterPanel.js';

// Je Grid (#grid-content / #magrid-content) Variante + Optionen des letzten Renderns, damit
// aktualisiereBedarfZeilen() die Zeile(n) nach einer Schichtänderung allein neu aufbauen kann.
const zeilenNachGrid = new Map();

function el(tag, className, text) {
	const e = document.createElement(tag);
	if (className) e.className = className;
	if (text !== undefined) e.textContent = text;
	return e;
}

const offen = (z) => Math.max(0, z.BedarfAnzahl - (z.filled || 0));
const zeitText = (z) => `${kurzZeit(z.SchichtVon)}–${kurzZeit(z.SchichtBis)}`;
const tagVon = (z) => dayjs(z.Datum).format('YYYY-MM-DD');

/**
 * Lädt die Bedarfszeilen der laufenden Woche (gefiltert wie die Personenzeilen, ohne Suche) und — nur wenn
 * ein offener Bedarf dazugehört — die Events (Farbe/Icon im Chip, „Event öffnen“ im Dialog).
 * @param {function} neuLaden baut die ganze Ansicht neu auf (nach Zuweisen im Dialog)
 */
async function ladeKontext(neuLaden) {
	const start = datumService.laufendesDatum;
	const alle = await bedarfService.holeBedarfZeitraum(start, start.endOf('week'));
	const bedarfe = alle.filter((z) => istBereichSichtbar(z.BereichID)
		&& istPostenSichtbar(z.PostenID)
		&& istQualifikationSichtbar(z.QualifikationID ? [z.QualifikationID] : [])
		&& istEinsatzortSichtbar(z.EinsatzortID ? [z.EinsatzortID] : []));
	const offene = bedarfe.filter((z) => offen(z) > 0)
		.sort((a, b) => new Date(a.SchichtVon) - new Date(b.SchichtVon) || (a.Bereich || '').localeCompare(b.Bereich || ''));
	const events = offene.some((z) => z.EventID) ? await eventService.holeEventsZeitraum(start, start.endOf('week')) : [];
	const eventById = new Map(events.map((e) => [e._id, e]));
	const oeffneBedarf = (bedarfID) => oeffneBedarfZuordnung(bedarfID, neuLaden, {
		onEventOeffnen: (eventID) => {
			const ev = eventById.get(eventID);
			if (ev) oeffneEventEditor(ev, start, neuLaden);
		},
	});
	return { start, bedarfe, offene, eventById, oeffneBedarf };
}

/** Ein Chip = eine offene Bedarfszeile (Posten × Tag × Zeitfenster). */
function baueChip(z, ev, onKlick) {
	const filled = z.filled || 0;
	const chip = el('button', `bopen${stateCls(filled, z.BedarfAnzahl) === 'part' ? ' part' : ''}${ev ? ' ev' : ''}`);
	chip.type = 'button';
	if (ev) chip.style.setProperty('--evf', eventFarbe(ev));
	const titel = `${z.Bereich || '—'} · ${z.Posten || '—'} ${zeitText(z)} — ${filled} von ${z.BedarfAnzahl} besetzt${ev ? ` · ${ev.Titel || 'Event'}` : ''} — klicken zum Zuweisen`;
	chip.title = titel;
	chip.setAttribute('aria-label', titel);

	const pn = el('span', 'pn', z.Posten || '—');
	if (z.Bereich) pn.appendChild(el('small', '', z.Bereich));
	chip.appendChild(pn);
	chip.appendChild(el('span', 'pc', `${filled}/${z.BedarfAnzahl}`));

	const tm = el('span', 'tm');
	if (ev) tm.appendChild(el('i', `bi bi-${ev.Icon || 'stars'}`));
	tm.appendChild(document.createTextNode(zeitText(z)));
	chip.appendChild(tm);

	const asg = el('span', 'asg');
	asg.innerHTML = '<i class="bi bi-person-plus-fill"></i>Zuweisen';
	chip.appendChild(asg);

	chip.addEventListener('click', onKlick);
	return chip;
}

/**
 * Baut die Zellen einer Bedarfszeile: Label · 7 Tageszellen · Wochensumme.
 * @param {object} kontext aus ladeKontext()
 * @param {HTMLElement} gridEl (für die Feiertags-Übernahme beim Neuaufbau)
 * @param {object} zeile
 * @param {array} zeile.bedarfe alle (sichtbaren) Bedarfszeilen dieser Zeile
 * @param {array} zeile.offene davon die offenen, sortiert
 * @param {string} zeile.idPrefix Präfix für die Zellen-IDs
 * @param {string} [zeile.bereichID] gesetzt in der Bereichs-Variante (data-bereich fürs Einklappen)
 * @returns {array<HTMLElement>}
 */
function baueZellen(kontext, gridEl, { bedarfe, offene, idPrefix, bereichID }) {
	const offenePositionen = offene.reduce((summe, z) => summe + offen(z), 0);
	const ausEvent = offene.filter((z) => z.EventID).length;
	const positionenGesamt = bedarfe.reduce((summe, z) => summe + z.BedarfAnzahl, 0);
	const gedeckt = offene.length === 0;
	const flach = gedeckt ? ' flach' : '';
	const zellen = [];

	// Label (Spalten Name + Status)
	const lbl = el('div', `blbl${gedeckt ? ' clear' : ''}`);
	lbl.id = `${idPrefix}lbl`;
	const icon = el('div', 'bic');
	icon.innerHTML = `<i class="bi ${gedeckt ? 'bi-clipboard2-check' : 'bi-clipboard2-x'}"></i>`;
	const text = el('div');
	// Bereichs-Variante (Wochenplan): Bereichsname mit anzeigen, die Mitarbeiteransicht hat eine Zeile über alle Bereiche
	const bereichName = bereichID ? bedarfe.find((z) => z.Bereich)?.Bereich : undefined;
	const titel = gedeckt ? 'Bedarf gedeckt' : 'Offener Bedarf';
	text.appendChild(el('div', 'bt', bereichName ? `${titel} · ${bereichName}` : titel));
	text.appendChild(el('div', 'bs', gedeckt
		? `alle ${positionenGesamt} Positionen besetzt`
		: `${offenePositionen} ${offenePositionen === 1 ? 'Position' : 'Positionen'}${ausEvent > 0 ? ` · ${ausEvent} aus Veranstaltung` : ''}`));
	lbl.append(icon, text);
	zellen.push(lbl);

	// 7 Tageszellen
	for (let i = 0; i < 7; i += 1) {
		const tag = kontext.start.add(i, 'days');
		const tagKey = tag.format('YYYY-MM-DD');
		const zelle = el('div', `bcell${i >= 5 ? ' we' : ''}${flach}`);
		zelle.id = `${idPrefix}${i + 1}`;
		// Gleiches aria-label wie die Tageszellen darunter, damit zeigeFeiertage() .feiertag setzt. Beim
		// Neuaufbau nach einer Schichtänderung läuft zeigeFeiertage() nicht erneut — dann von dort übernehmen.
		const datumLabel = tag.format('DD.MM.YYYY');
		zelle.setAttribute('aria-label', datumLabel);
		if (gridEl.querySelector(`.daycell.feiertag[aria-label="${datumLabel}"]`)) zelle.classList.add('feiertag');
		const offeneTag = offene.filter((z) => tagVon(z) === tagKey);
		if (offeneTag.length === 0) {
			const hatBedarf = bedarfe.some((z) => tagVon(z) === tagKey);
			zelle.classList.add('clear');
			const hinweis = el('span', hatBedarf ? 'clr' : 'clr leer', hatBedarf ? 'voll besetzt' : 'kein Bedarf');
			if (hatBedarf) hinweis.insertAdjacentHTML('afterbegin', '<i class="bi bi-check-circle-fill"></i>');
			zelle.appendChild(hinweis);
		} else {
			offeneTag.forEach((z) => {
				const ev = z.EventID ? kontext.eventById.get(z.EventID) : null;
				zelle.appendChild(baueChip(z, ev, () => kontext.oeffneBedarf(z._id)));
			});
		}
		zellen.push(zelle);
	}

	// Wochensumme
	const sum = el('div', `bsum${gedeckt ? ' clear' : ''}${flach}`);
	sum.id = `${idPrefix}sum`;
	sum.innerHTML = gedeckt ? '<i class="bi bi-check-lg"></i><span>gedeckt</span>' : `<b>${offenePositionen}</b><span>offen</span>`;
	zellen.push(sum);

	zellen.forEach((z) => {
		z.dataset.bedarfzeile = idPrefix;
		if (bereichID) z.dataset.bereich = bereichID;
	});
	return zellen;
}

/**
 * Mitarbeiteransicht: eine Bedarfszeile über alle Bereiche direkt hinter den Spaltenköpfen. Eine bereits
 * vorhandene Zeile wird ersetzt. Ohne sichtbare Bedarfszeilen in der Woche: keine Zeile.
 * @param {HTMLElement} gridEl #magrid-content
 * @param {object} optionen
 * @param {function} optionen.neuLaden baut die ganze Ansicht neu auf
 * @param {string} [optionen.idPrefix] Präfix für die Zellen-IDs
 */
async function erstelleBedarfZeile(gridEl, optionen) {
	const { neuLaden, idPrefix = 'mabedarf:' } = optionen;
	const kontext = await ladeKontext(neuLaden);
	gridEl.querySelectorAll('[data-bedarfzeile]').forEach((z) => z.remove());
	zeilenNachGrid.set(gridEl, { modus: 'woche', optionen });
	if (kontext.bedarfe.length === 0) return;

	const zellen = baueZellen(kontext, gridEl, { bedarfe: kontext.bedarfe, offene: kontext.offene, idPrefix });
	// Direkt hinter die letzte Spaltenkopf-Zelle ("Woche") einfügen.
	const kopfZellen = gridEl.querySelectorAll('.colhead');
	const letzterKopf = kopfZellen[kopfZellen.length - 1];
	if (letzterKopf) {
		letzterKopf.after(...zellen);
	} else {
		gridEl.append(...zellen);
	}
}

/**
 * Wochenplan, Schritt 1: lädt die Bedarfe einmal pro Render, entfernt alte Bereichs-Bedarfszeilen und
 * merkt sich das Grid für aktualisiereBedarfZeilen(). Danach je Bereich fuegeBereichBedarfZeileEin().
 * @param {HTMLElement} gridEl #grid-content
 * @param {object} optionen
 * @param {function} optionen.neuLaden baut die ganze Ansicht neu auf
 * @returns {Promise<object>} Kontext für fuegeBereichBedarfZeileEin
 */
async function ladeBereichBedarfe(gridEl, optionen) {
	const kontext = await ladeKontext(optionen.neuLaden);
	gridEl.querySelectorAll('[data-bedarfzeile]').forEach((z) => z.remove());
	zeilenNachGrid.set(gridEl, { modus: 'bereich', optionen });
	return kontext;
}

/**
 * Wochenplan, Schritt 2: fügt die Bedarfszeile eines Bereichs direkt unter dessen Kopf ein — nur, wenn der
 * Bereich offene Bedarfe hat. Ist der Bereich eingeklappt, startet die Zeile ebenfalls ausgeblendet.
 * @param {HTMLElement} gridEl #grid-content
 * @param {string} bereichID
 * @param {object} kontext aus ladeBereichBedarfe()
 */
function fuegeBereichBedarfZeileEin(gridEl, bereichID, kontext) {
	const kopf = gridEl.querySelector(`[id="${bereichID}-bereich-header"]`);
	const offene = kontext.offene.filter((z) => z.BereichID === bereichID);
	if (!kopf || offene.length === 0) return;
	const zellen = baueZellen(kontext, gridEl, {
		bedarfe: kontext.bedarfe.filter((z) => z.BereichID === bereichID),
		offene,
		idPrefix: `bedarf:${bereichID}:`,
		bereichID,
	});
	if (kopf.classList.contains('area-header-closed')) zellen.forEach((z) => z.classList.add('hidden'));
	kopf.after(...zellen);
}

/**
 * Baut die Bedarfszeile(n) der sichtbaren Ansicht neu auf — nach einer Schichtänderung im Raster, die
 * einen Bedarf betrifft (Schicht mit BedarfID gelöscht/verschoben), ohne die ganze Ansicht neu zu laden.
 */
async function aktualisiereBedarfZeilen() {
	for (const [gridEl, { modus, optionen }] of zeilenNachGrid) {
		if (!gridEl.isConnected || gridEl.offsetParent === null) continue;
		if (modus === 'woche') {
			await erstelleBedarfZeile(gridEl, optionen);
		} else {
			// Entfernen und Einfügen ohne await dazwischen — sonst könnten sich zwei kurz aufeinanderfolgende
			// Aktualisierungen (z.B. Umsetzen: alte Schicht weg + neue Schicht da) zu doppelten Zeilen verschränken.
			const kontext = await ladeKontext(optionen.neuLaden);
			gridEl.querySelectorAll('[data-bedarfzeile]').forEach((z) => z.remove());
			gridEl.querySelectorAll('.bereich-header').forEach((kopf) => {
				fuegeBereichBedarfZeileEin(gridEl, kopf.id.replace(/-bereich-header$/, ''), kontext);
			});
		}
	}
}

export { erstelleBedarfZeile, ladeBereichBedarfe, fuegeBereichBedarfZeileEin, aktualisiereBedarfZeilen };
